// [343] EL REPASO DE CATEGORÍAS, COMPARTIDO POR LA LISTA Y POR LA FICHA.
//
// Estaba dentro de `AlumnosPage`. Se saca aquí porque ahora lo abre también la
// ficha del alumno, y DOS COPIAS DE ESTE MODAL SERÍAN DOS PUERTAS que escriben
// categorías: la que se arregla y la que se olvida. Los `data-testid` se
// conservan tal cual (`rederiv-*`), que es lo que mide la suite de la lista.
//
// Lo que NO hace, a propósito: calcular nada. El recuento y las dos poblaciones
// las da el servidor, que es quien ve a todos los alumnos y su `audit_log`.
//
// ⚠️ Y NO HAY «aplicar solo a este alumno». `admin_rederivar_categorias` recibe
// `(p_aplicar, p_incluir_corregidas)` y nada más: no tiene parámetro de alumno.
// Así que la ficha abre ESTE MISMO preview, con su lista delante, y quien mira
// ve exactamente a quién va a tocar antes de tocarlo. Un botón de la ficha que
// escribiera de golpe sería una segunda puerta sin preview, que es justo lo que
// se decidió no tener cuando la re-derivación se hizo botón y no cron.

import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import Modal from './ui/Modal';
import { CATEGORY_LABELS } from '../lib/categorias';

// Lo que devuelve `admin_rederivar_categorias_preview`: DOS poblaciones.
// `cambios` son los que nadie tocó a mano; `corregidas`, los que tienen la
// categoría puesta por una persona —el que juega con los mayores— y que por
// defecto NO se tocan.
export interface RederivFila {
  user_id: string;
  full_name: string | null;
  anio: number;
  categoria_actual: string | null;
  categoria_nueva: string;
}

export interface RederivPreview {
  n: number;
  cambios: RederivFila[];
  n_corregidas: number;
  corregidas: RederivFila[];
}

export type LecturaPreview =
  | { estado: 'ok'; preview: RederivPreview }
  | { estado: 'error'; mensaje: string };

// Un fallo de lectura NO es «no hay cambios»: son cosas distintas y la
// diferencia importa, porque la segunda hace que nadie vuelva a mirar. Por eso
// esto devuelve un estado y no un `RederivPreview | null`.
export async function leerPreview(): Promise<LecturaPreview> {
  const { data, error } = await supabase.rpc('admin_rederivar_categorias_preview');
  const r = data as unknown as ({ ok: boolean; error?: string } & RederivPreview) | null;
  if (error || !r?.ok) {
    return {
      estado: 'error',
      mensaje: error?.message ?? r?.error ?? 'No se pudo leer el repaso de categorías.',
    };
  }
  return {
    estado: 'ok',
    preview: {
      n: r.n,
      cambios: r.cambios ?? [],
      n_corregidas: r.n_corregidas,
      corregidas: r.corregidas ?? [],
    },
  };
}

/** La fila de ESE alumno, si el servidor lo ve desfasado. En cuál de las dos
 *  poblaciones cae importa: el que está puesto a mano se avisa distinto, porque
 *  su categoría está así a propósito. */
export function filaDelAlumno(
  p: RederivPreview,
  userId: string
): { fila: RederivFila; aMano: boolean } | null {
  const enCambios = p.cambios.find((c) => c.user_id === userId);
  if (enCambios) return { fila: enCambios, aMano: false };
  const enCorregidas = p.corregidas.find((c) => c.user_id === userId);
  if (enCorregidas) return { fila: enCorregidas, aMano: true };
  return null;
}

function etiqueta(cat: string | null): string {
  if (!cat) return 'sin categoría';
  return CATEGORY_LABELS[cat] ?? cat;
}

function Linea({ c }: { c: RederivFila }) {
  return (
    <p key={c.user_id} className="text-xs text-text-secondary">
      {c.full_name ?? c.user_id} ({c.anio}): {etiqueta(c.categoria_actual)} →{' '}
      {etiqueta(c.categoria_nueva)}
    </p>
  );
}

export default function RevisarCategoriasModal({
  open,
  onClose,
  onAplicado,
}: {
  open: boolean;
  onClose: () => void;
  /** Se llama tras aplicar, para que quien abrió el modal recargue lo suyo. */
  onAplicado?: () => void | Promise<void>;
}) {
  const [preview, setPreview] = useState<RederivPreview | null>(null);
  const [error, setError] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [incluirCorregidas, setIncluirCorregidas] = useState(false);

  // Se pide AL ABRIR, y se descarta la respuesta si entretanto se cerró: si no,
  // una lectura lenta repintaría un modal que ya no está, o peor, pisaría la
  // siguiente apertura con datos viejos.
  useEffect(() => {
    if (!open) return;
    let vivo = true;
    setError('');
    setPreview(null);
    setIncluirCorregidas(false);
    void leerPreview().then((r) => {
      if (!vivo) return;
      if (r.estado === 'error') setError(r.mensaje);
      else setPreview(r.preview);
    });
    return () => {
      vivo = false;
    };
  }, [open]);

  async function aplicar() {
    setAplicando(true);
    setError('');
    const { data, error: err } = await supabase.rpc('admin_rederivar_categorias', {
      p_aplicar: true,
      p_incluir_corregidas: incluirCorregidas,
    });
    const r = data as unknown as { ok: boolean; error?: string; cambiados?: number } | null;
    setAplicando(false);
    if (err || !r?.ok) {
      setError(err?.message ?? r?.error ?? 'No se pudo aplicar.');
      return;
    }
    onClose();
    await onAplicado?.();
  }

  return (
    <Modal open={open} onClose={onClose} title="Revisar categorías" size="lg">
      {error ? (
        <p className="text-error text-sm" data-testid="rederiv-error">
          {error}
        </p>
      ) : !preview ? (
        <p className="text-text-secondary text-sm">Leyendo…</p>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-sm text-white mb-2" data-testid="rederiv-n">
              {preview.n === 0
                ? 'Ninguna categoría se ha quedado desfasada.'
                : `${preview.n} ${preview.n === 1 ? 'alumno' : 'alumnos'} con la categoría desfasada respecto a su año.`}
            </p>
            {preview.cambios.map((c) => (
              <Linea key={c.user_id} c={c} />
            ))}
          </div>

          {/* La segunda lista NO se propone: si alguien movió a un chaval a mano
              —el que juega con los mayores— y el botón propusiera devolverlo a
              su año, haría falta que quien mira se acordara de por qué lo movió.
              Aquí se le enseña aparte y decide. */}
          {preview.n_corregidas > 0 && (
            <div className="border-t border-bg-border pt-4">
              <p className="text-sm text-white mb-1" data-testid="rederiv-corregidas">
                {preview.n_corregidas} {preview.n_corregidas === 1 ? 'tiene' : 'tienen'} la
                categoría puesta a mano
              </p>
              <p className="text-xs text-text-secondary mb-2">
                No se tocan salvo que lo marques: alguien los movió a propósito.
              </p>
              {preview.corregidas.map((c) => (
                <Linea key={c.user_id} c={c} />
              ))}
              <label className="flex items-center gap-2 mt-2 text-xs text-text-secondary">
                <input
                  type="checkbox"
                  data-testid="rederiv-incluir"
                  checked={incluirCorregidas}
                  onChange={(e) => setIncluirCorregidas(e.target.checked)}
                />
                Mover también a los que están puestos a mano
              </label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost text-sm" onClick={onClose}>
              Cerrar
            </button>
            <button
              className="btn-gold text-sm"
              data-testid="rederiv-aplicar"
              disabled={
                aplicando || (preview.n === 0 && !(incluirCorregidas && preview.n_corregidas > 0))
              }
              onClick={aplicar}
            >
              {aplicando ? 'Aplicando…' : 'Aplicar'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
